import { type ReactNode } from 'react';
import { Scale } from 'lucide-react';
import { cn } from '../../ui';
import { AiSpark } from './spark';

/**
 * Shared chrome for the assistant answer cards so all seven look like one
 * family: a compact `bg-panel` card, a header with an AI spark + mono tool
 * label, and a primary-tinted legal-citation chip. Presentational only.
 */

export function CardShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-xl border border-line bg-panel p-3.5', className)}>{children}</div>
  );
}

/** Card header: a serif title on the left, an AI-tagged mono label on the right. */
export function CardHeader({ title, label }: { title: ReactNode; label: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 font-serif text-sm font-medium leading-snug text-ink">{title}</div>
      <span className="flex shrink-0 items-center gap-1 pt-0.5 font-mono text-[9.5px] uppercase tracking-wider text-ink-faint">
        <AiSpark className="h-3 w-3" />
        {label}
      </span>
    </div>
  );
}

/** Legal-citation chip (mono, primary tint) shown at the foot of a card. */
export function CiteChip({ children }: { children: ReactNode }) {
  return (
    <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary/5 px-2.5 py-1.5 font-mono text-[10px] leading-none text-primary-dark">
      <Scale className="h-3 w-3 shrink-0" strokeWidth={1.75} />
      {children}
    </div>
  );
}
